import React, { useState } from "react";
import { Edit, ContentCopy, Delete, MoreVert } from "@mui/icons-material";
import { Menu, MenuItem, Tooltip, Dialog, Button } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Workspace } from "../../hooks/useWorkspacesSimple";
import createDocumentClone from "@/components/FpAppV4/services/createDocumentClone";
import { deleteWorkspaceById } from "@/components/FpAppV4/services/updateWorkspace";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL } from "@/constants";
import { useQueryClient } from "@tanstack/react-query";
import { toConversationSlug } from "@/components/FpPages/Chat/chatUtils";

interface HistoryItemProps {
  workspace: Workspace;
  currentWorkspaceId: string;
  createNewThread: () => void;
  workspaceType?: string;
  parentApp?: string;
  parentAppUrl?: string;
  closeDrawer?: () => void;
}

const HistoryItem: React.FC<HistoryItemProps> = ({
  workspace,
  currentWorkspaceId,
  createNewThread,
  workspaceType,
  parentApp,
  parentAppUrl,
  closeDrawer,
}) => {
  if (!workspaceType) workspaceType = "private_doc_editor";
  if (!parentAppUrl) parentAppUrl = "/chat";
  if (!parentApp) parentApp = "Chat";
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [openChangeTitleDialog, setOpenChangeTitleDialog] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const deleteElement = async () => {
    await deleteWorkspaceById(workspace.id);
    queryClient.invalidateQueries({ queryKey: ["chatHistory"] });
    (window as Window).openFpToast("Document deleted", "success");
    if (currentWorkspaceId === workspace.id) {
      navigate(parentAppUrl || "/chat");
    }
  };

  const saveNewTitle = async () => {
    try {
      await fpFetch(`${BASE_API_URL}/workspaces/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ _id: workspace.id, title: newTitle }),
      });
      queryClient.invalidateQueries({ queryKey: ["chatHistory"] });
      setOpenChangeTitleDialog(false);
      handleCloseMenu();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
      >
        <MenuItem
          onClick={() => {
            setOpenChangeTitleDialog(true);
            setNewTitle(workspace.title || "");
          }}
        >
          <span className="p-1 px-2">
            <Edit sx={{ fontSize: "14px", marginRight: "5px" }} />
            Rename
          </span>
        </MenuItem>
        <MenuItem
          onClick={() => {
            createDocumentClone(workspace.id, true, true);
            handleCloseMenu();
          }}
        >
          <span className="p-1 px-2">
            <ContentCopy sx={{ fontSize: "14px", marginRight: "5px" }} />
            Create Copy
          </span>
        </MenuItem>
        <MenuItem
          onClick={() => {
            deleteElement();
            handleCloseMenu();
          }}
        >
          <span className="p-1 px-2 text-red-500">
            <Delete sx={{ fontSize: "14px", marginRight: "5px" }} />
            Delete
          </span>
        </MenuItem>
      </Menu>

      <Dialog
        open={openChangeTitleDialog}
        onClose={() => {
          setOpenChangeTitleDialog(false);
          handleCloseMenu();
        }}
      >
        <div className="w-[400px] h-[150px] flex items-center">
          <div className="p-4 w-full">
            <input
              className="w-full p-2 border border-gray-300 rounded outline-none"
              placeholder="Enter new title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
            <div className="flex justify-end mt-4">
              <Button onClick={() => setOpenChangeTitleDialog(false)}>
                Cancel
              </Button>
              <Button onClick={saveNewTitle}>Save</Button>
            </div>
          </div>
        </div>
      </Dialog>
      <Link
        to={`${parentAppUrl}/${
          parentApp === "Chat" ? toConversationSlug(workspace.id) : workspace.id
        }`}
        onClick={() => {
          if (closeDrawer) {
            closeDrawer();
          }
        }}
      >
        <div
          className={`text-[14px] group transition-all p-2 hover:bg-gray-100 rounded cursor-pointer
            ${
              workspace.id === currentWorkspaceId
                ? "border-r-4 border-r-blue-800 bg-gray-50 rounded-r-none"
                : ""
            }`}
        >
          <div className="flex justify-between items-center">
            <div className="flex flex-col overflow-hidden">
              <Tooltip
                title={workspace.title || `New ${parentApp}`}
                leaveDelay={0}
                PopperProps={{
                  disablePortal: true,
                  sx: { pointerEvents: "none" },
                }}
              >
                <span className="font-medium text-gray-700 overflow-hidden text-ellipsis line-clamp-1">
                  {workspace.title || `New ${parentApp}`}
                </span>
              </Tooltip>
              {workspace.subtitle && (
                <Tooltip
                  title={workspace.subtitle}
                  leaveDelay={0}
                  PopperProps={{
                    disablePortal: true,
                    sx: { pointerEvents: "none" },
                  }}
                >
                  <span className="text-[11px] text-gray-500 overflow-hidden text-ellipsis line-clamp-1">
                    {workspace.subtitle}
                  </span>
                </Tooltip>
              )}
            </div>
            <span
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuAnchor(e.currentTarget);
              }}
              className="opacity-0 group-hover:opacity-100 transition-all duration-300 ml-2"
            >
              <Tooltip
                title="Options"
                leaveDelay={0}
                PopperProps={{
                  disablePortal: true,
                  sx: { pointerEvents: "none" },
                }}
              >
                <MoreVert
                  style={{ fontSize: "18px" }}
                  className="text-gray-400"
                />
              </Tooltip>
            </span>
          </div>
        </div>
      </Link>
    </>
  );
};

export default HistoryItem;
